import {useInfiniteQuery} from '@tanstack/react-query';
import {fetchRecords} from '../services/BillableEventsService';
import {useEffect, useState} from 'react';

// FNP-533: read-only module. No delete/create/update mutations — the metering
// pipeline owns the BillableEvent lifecycle. This hook is list-fetch only.

// Module-scoped global filter store (global-filter-store-pattern). Fields:
//   state         -> EmissionState (drives listBillableEventsByState when alone)
//   channel       -> BillingChannel (with day range -> listBillableEventsByChannelAndDay)
//   customerId    -> ID (BillableEventsFilterInput)
//   meteringRecordId -> String (BillableEventsFilterInput)
//   dayStart / dayEnd / createdBefore -> AWSDateTime bounds
// No default filter is seeded — admins see all events on first visit.
let globalFilters: Record<string, string> = {};
const filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useBillableEventsFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => setFilters(newFilters);
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {filters, handleSetFilters, handleClearFilters};
};

export const useBillableEvents = () => {
    const {filters} = useBillableEventsFilter();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);

    const sort = {column: 'created', direction: 'desc' as const};

    // INFINITE QUERY — filter object is part of the cache key, so changing the
    // filter refetches automatically and routes to the right backend query.
    const billableEventsQuery = useInfiniteQuery({
        queryKey: ['billableEvents', keyword, filters, limit, sort.column, sort.direction],
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            fetchRecords({limit, sort, filter: filters, nextToken: pageParam}),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const fetchRecordsAction = () => {
        if (billableEventsQuery.hasNextPage) {
            billableEventsQuery.fetchNextPage();
        }
    };

    const reload = () => {
        billableEventsQuery.refetch();
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = billableEventsQuery.data?.pages?.flatMap(page => (page && 'items' in page) ? page.items : []) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: billableEventsQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: billableEventsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        reload,
        handleFilterByKeyword,
    };
};
